import dayjs from 'dayjs';

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import ChartHeader from 'components/charts/ChartHeader';
import { DataCard } from 'components/charts/DataCards';
import BarChartV2 from 'components/charts/chartJS/BarChart';
import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import DateInput from 'components/field/DateInput';
import EnumSelect from 'components/field/EnumSelect';
import Card from 'components/global/Card';
import Popper from 'components/global/Popper';
import Typography from 'components/global/Typography';
import { FilterAction, FilterType } from 'components/list/DynamicListFilterUtils';

import { ThreeCardsInRow, TwoCardsInRow } from 'pages/Dashboard/Dashboard.styled';
import { PopperMenu, PopperMenuItem } from 'pages/Shipments/Operation/AggregatorAging/AggregatorAging.styled';

import { AUTOCOMPLETE, ENUMS } from 'services/consts';

import { colorCombinations } from 'utils/colors';
import PATHS, { mergePaths } from 'utils/paths';

import { faTruckClock } from '@fortawesome/pro-regular-svg-icons';

import * as S from './OverviewCharts.styled';
import { getAnalysisClientData } from './SLAOverviewSlice';

const FILTERS = {
	shipmentCreatedDate: 'shipmentCreatedDate',
	originCountry: 'originCountry',
	originCity: 'originCity',
	destinationCountry: 'destinationCountry',
	destinationCity: 'destinationCity',
	deliveryCompanyName: 'deliveryCompanyName',
	shipmentFlow: 'shipmentFlow',
	serviceType: 'serviceType'
};

const FILTER_LIST = {
	[FILTERS.shipmentCreatedDate]: {
		key: [FILTERS.shipmentCreatedDate],
		action: FilterAction.between,
		filterType: FilterType.date,
		from: dayjs().subtract(90, 'day').endOf('day').toISOString(),
		to: dayjs().endOf('day').toISOString()
	},
	[FILTERS.originCountry]: {
		key: [FILTERS.originCountry],
		action: FilterAction.is,
		filterType: FilterType.enum,
		value: []
	},
	[FILTERS.originCity]: {
		key: [FILTERS.originCity],
		action: FilterAction.is,
		filterType: FilterType.enum,
		value: []
	},
	[FILTERS.destinationCity]: {
		key: [FILTERS.destinationCity],
		action: FilterAction.is,
		filterType: FilterType.enum,
		value: []
	},
	[FILTERS.destinationCountry]: {
		key: [FILTERS.destinationCountry],
		action: FilterAction.is,
		filterType: FilterType.enum,
		value: []
	},
	[FILTERS.deliveryCompanyName]: {
		key: [FILTERS.deliveryCompanyName],
		action: FilterAction.is,
		filterType: FilterType.enum,
		value: []
	},
	[FILTERS.shipmentFlow]: {
		key: [FILTERS.shipmentFlow],
		action: FilterAction.is,
		filterType: FilterType.enum,
		value: []
	},
	[FILTERS.serviceType]: {
		key: [FILTERS.serviceType],
		action: FilterAction.is,
		filterType: FilterType.multiSelection,
		value: []
	}
};

const OverviewCharts = () => {
	const dispatch = useDispatch();
	const analysisData = useSelector((state) => state.slaOverview.analysis);
	const [filtersMenuButtonRef, setFilterMenuButtonRef] = useState(null);
	const [filters, setFilter] = useState(JSON.parse(JSON.stringify(FILTER_LIST)));
	const { t } = useTranslation();
	const navigate = useNavigate();

	useEffect(() => {
		const filterList = [];
		Object.keys(filters).forEach((key) => {
			const filterObj = filters[key];
			if (filterObj?.value?.length === 0 && !filterObj?.from) {
				return;
			}
			filterList.push(filterObj);
		});
		dispatch(getAnalysisClientData(filterList));
	}, [filters]);

	const changeFilter = (key, value, type = 'string') => {
		if (value === null || (type === 'date' && !value.from && !value.to)) {
			setFilter((prev) => {
				const newFilter = { ...prev };
				delete newFilter[key];
				return newFilter;
			});
			return;
		}
		setFilter((prev) => ({
			...prev,
			[key]: {
				...FILTER_LIST[key],
				...prev[key],
				...(type === 'string' ? { value } : { to: value.to, from: value.from })
			}
		}));
	};

	const handleNumberCard = (key) => {
		const filter = {
			accessor: 'delayType',
			action: FilterAction.is,
			value: []
		};
		switch (key) {
			case 'pickupAging':
				filter.value = ['pickupDelay', 'reversePickupDelay'];
				break;
			case 'deliveryAging':
				filter.value = ['deliveryDelay', 'reverseDeliveryDelay'];
				break;
			case 'totalAging':
				filter.value = ['pickupDelay', 'reversePickupDelay', 'deliveryDelay', 'reverseDeliveryDelay'];
				break;
			default:
				break;
		}
		navigate(mergePaths(PATHS.ANALYSIS, PATHS.ANALYSIS_TAB), {
			state: {
				cardFilter: filter,
				analysisFilters: Object.keys(filters)
					.filter((key) => filters[key].value && filters[key].value.length > 0 && key !== 'serviceType')
					.map((key) => ({
						accessor: key === 'deliveryCompanyName' ? 'deliveryCompanySettingsName' : key,
						action: FilterAction.is,
						value: filters[key].value
					}))
			}
		});
	};

	const NUMBERS_CARDS_CONFIG = [
		{
			accessor: 'totalPickupCount',
			label: 'pendingPickup',
			customIcon: 'fa-regular-box-clock',
			backgroundColor: '#FF950014',
			color: '#FF9500',
			onButtonClick: () => handleNumberCard('pickupAging')
		},
		{
			accessor: 'totalDeliveryCount',
			label: 'pendingDelivery',
			icon: faTruckClock,
			backgroundColor: '#AB47BC14',
			color: '#AB47BC',
			onButtonClick: () => handleNumberCard('deliveryAging')
		},
		{
			accessor: 'totalCount',
			label: 'totalPending',
			customIcon: 'fa-solid fa-truck-fast',
			backgroundColor: '#256CF014',
			color: '#256CF0',
			onButtonClick: () => handleNumberCard('totalAging')
		}
	];

	const sortByCount = useCallback((itemList, prefix) => {
		const sortedItems = itemList.sort((a, b) => parseInt(a.replace(prefix, '')) - parseInt(b.replace(prefix, '')));

		const dayItems = sortedItems.filter((item) => item.includes('Day'));
		const weekItems = sortedItems.filter((item) => item.includes('Week'));

		return dayItems.concat(weekItems);
	});

	const prepareChartData = (data, prefix) => {
		if (analysisData.isLoading) {
			return {
				labels: [t('loading')],
				datasets: [{ data: [0] }]
			};
		}

		const notTranslatedLabels = sortByCount(Object.keys(data ?? {}), prefix);
		try {
			const labels = notTranslatedLabels.map((item) => t(item));
			const datasets = [
				{
					data: notTranslatedLabels.map((item) => data[item]),
					backgroundColor: notTranslatedLabels.map(
						(_, index) =>
							colorCombinations[
								index < 2
									? colorCombinations.length - 1
									: index < 8
										? colorCombinations.length - 2
										: colorCombinations.length - 3
							].color
					),
					borderColor: notTranslatedLabels.map(
						(_, index) =>
							colorCombinations[
								index < 2
									? colorCombinations.length - 1
									: index < 8
										? colorCombinations.length - 2
										: colorCombinations.length - 3
							].color
					)
				}
			];
			return { labels, datasets };
		} catch {
			return {
				labels: [],
				datasets: [{ data: [0] }]
			};
		}
	};

	return (
		<S.Container>
			<div>
				<ChartHeader
					title={'overview'}
					showDivider={false}
					filter={
						<>
							<Button
								id="filters-button"
								lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/filter-icon.json.gz"
								ref={setFilterMenuButtonRef}
								variant="borderless"
								label="allFilters"
							/>
							{filtersMenuButtonRef && (
								<Popper refElement={filtersMenuButtonRef} placement="bottom-end">
									{() => (
										<PopperMenu>
											<PopperMenuItem>
												<Typography variant="p2" fontWeight="500">
													shipmentCreatedDate
												</Typography>
												<DateInput
													value={{
														start: filters?.[FILTERS.shipmentCreatedDate]?.from,
														end: filters?.[FILTERS.shipmentCreatedDate]?.to
													}}
													setValue={(date) =>
														changeFilter(
															FILTERS.shipmentCreatedDate,
															{ from: date?.start, to: date?.end },
															'date'
														)
													}
													rangeMode
												/>
											</PopperMenuItem>
											<PopperMenuItem>
												<Typography variant="p2" fontWeight="500">
													originCountry
												</Typography>
												<EnumSelect
													enumName={ENUMS.COUNTRIES}
													value={filters[FILTERS.originCountry]?.value}
													setValue={(value) =>
														changeFilter(FILTERS.originCountry, value ? [value] : null)
													}
													labelPrefix="country."
												/>
											</PopperMenuItem>

											<PopperMenuItem>
												<Typography variant="p2" fontWeight="500">
													originCity
												</Typography>
												<AutoComplete
													autoCompleteName={AUTOCOMPLETE.CITIES_LIST}
													setValue={(value) => {
														changeFilter(
															FILTERS.originCity,
															value?.name ? [value.name] : null
														);
													}}
													selectionLabelAccessor={['name', 'country']}
													columns={[
														{ accessor: 'name', width: 200 },
														{ accessor: 'country', width: 100 }
													]}
												/>
											</PopperMenuItem>
											<PopperMenuItem>
												<Typography variant="p2" fontWeight="500">
													destinationCountry
												</Typography>
												<EnumSelect
													enumName={ENUMS.COUNTRIES}
													value={filters[FILTERS.destinationCountry]?.value}
													setValue={(value) =>
														changeFilter(FILTERS.destinationCountry, value ? [value] : null)
													}
													labelPrefix="country."
												/>
											</PopperMenuItem>

											<PopperMenuItem>
												<Typography variant="p2" fontWeight="500">
													destinationCity
												</Typography>
												<AutoComplete
													autoCompleteName={AUTOCOMPLETE.CITIES_LIST}
													setValue={(value) => {
														changeFilter(
															FILTERS.destinationCity,
															value?.name ? [value.name] : null
														);
													}}
													selectionLabelAccessor={['name', 'country']}
													columns={[
														{ accessor: 'name', width: 200 },
														{ accessor: 'country', width: 100 }
													]}
												/>
											</PopperMenuItem>
											<PopperMenuItem>
												<Typography variant="p2" fontWeight="500">
													deliveryCompanyName
												</Typography>
												<EnumSelect
													enumName={ENUMS.DELIVERY_COMPANY_SETTINGS_FOR_AGGREGATORS_WITH_OMNI}
													value={filters[FILTERS.deliveryCompanyName]?.value}
													setValue={(value) =>
														changeFilter(
															FILTERS.deliveryCompanyName,
															value ? [value] : null
														)
													}
												/>
											</PopperMenuItem>
											<PopperMenuItem>
												<Typography variant="p2" fontWeight="500">
													shipmentFlow
												</Typography>
												<EnumSelect
													enumName={ENUMS.SHIPMENT_FLOW}
													value={filters[FILTERS.shipmentFlow]?.value}
													setValue={(value) =>
														changeFilter(FILTERS.shipmentFlow, value ? [value] : null)
													}
												/>
											</PopperMenuItem>
											<PopperMenuItem>
												<Typography variant="p2" fontWeight="500">
													serviceType
												</Typography>
												<EnumSelect
													enumName={ENUMS.PRICE_CONFIGURATION_SERVICE_TYPE}
													value={filters[FILTERS.serviceType]?.value || []}
													multipleSelection
													setValue={(value) =>
														changeFilter(FILTERS.serviceType, value ? value : null)
													}
												/>
											</PopperMenuItem>
										</PopperMenu>
									)}
								</Popper>
							)}
						</>
					}
				/>
				<ThreeCardsInRow>
					{NUMBERS_CARDS_CONFIG.map((config) => (
						<DataCard
							key={config.accessor}
							loading={analysisData.isLoading}
							cardConfig={config}
							data={analysisData.data?.totalNumbers?.[config.accessor] ?? '-'}
						/>
					))}
				</ThreeCardsInRow>
			</div>
			<TwoCardsInRow wideCharts>
				<Card>
					<ChartHeader title="pickupAging" />
					<BarChartV2 data={prepareChartData(analysisData.data?.pickupCounts, 'count')} />
				</Card>
				<Card>
					<ChartHeader title="deliveryAging" />
					<BarChartV2 data={prepareChartData(analysisData.data?.deliveryCounts, 'count')} />
				</Card>
			</TwoCardsInRow>
		</S.Container>
	);
};

export default OverviewCharts;
